import React, { useState, useEffect } from 'react';
import financialService from '../../services/financialService';
import AdvancedDataTable from '../ui/AdvancedDataTable';
import './FinancialDashboard.css';

const DetailedDataTable = ({ marketplace, cacheKey, fuentePrecio = 'Publications (actual)', horizonteTemporal = 'Mensual', tipoVolumen = 'Predicciones' }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [availableProviders, setAvailableProviders] = useState([]);
  const [error, setError] = useState(null);
  
  const [filters, setFilters] = useState({
    proveedor_filter: null,
    min_utility: '',
    max_utility: '',
    limit: 50,
    offset: 0
  });
  
  useEffect(() => {
    loadData();
  }, [marketplace, cacheKey, filters, horizonteTemporal, tipoVolumen]);
  
  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await financialService.getDetailedData(
        marketplace, 
        cacheKey, 
        filters,
        horizonteTemporal,
        tipoVolumen
      );
      
      if (result.success) {
        setData(result.filtered_data || []);
        setPagination(result.pagination);
        setAvailableProviders(result.available_providers || []);
      }
    } catch (err) {
      console.error('Error cargando datos detallados:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      offset: 0 // Reset offset al cambiar filtros
    }));
  };
  
  const handlePageChange = (newOffset) => {
    setFilters(prev => ({
      ...prev,
      offset: newOffset
    }));
  };
  
  const handleExport = () => {
    const url = financialService.getExportCsvUrl(marketplace, cacheKey, filters);
    window.open(url, '_blank');
  };
  
  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-';
    const num = typeof val === 'number' ? val : parseFloat(val);
    if (isNaN(num)) return '-';
    return `$${num.toFixed(2)}`;
  };
  
  const formatNumber = (val) => {
    if (val === null || val === undefined) return '-';
    const num = typeof val === 'number' ? val : parseFloat(val);
    if (isNaN(num)) return '-';
    return num.toFixed(2);
  };
  
  const columns = [
    { field: 'identificador', headerName: 'Identificador', width: 180, minWidth: 150 },
    { field: 'sku', headerName: 'SKU', width: 150 },
    { field: 'proveedor', headerName: 'Proveedor', width: 180 },
    { field: 'product_name', headerName: 'Producto', width: 300, minWidth: 200 },
    { field: 'precio', headerName: 'Precio', valueFormatter: (params) => formatCurrency(params.value), width: 120 },
    { field: 'costo', headerName: 'Costo', valueFormatter: (params) => formatCurrency(params.value), width: 120 },
    { field: 'utilidad_neta', headerName: 'Utilidad Neta', valueFormatter: (params) => formatCurrency(params.value), width: 140 },
    { field: 'venta_historica_promedio', headerName: 'Ventas Hist.', valueFormatter: (params) => formatNumber(params.value), width: 120 },
    { field: 'prediccion_promedio', headerName: 'Predicción', valueFormatter: (params) => formatNumber(params.value), width: 120 }
  ];
  
  if (loading && !data.length) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando datos detallados...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-container">
        <p>❌ {error}</p>
        <button onClick={loadData} className="btn btn-primary">
          🔄 Reintentar
        </button>
      </div>
    );
  }
  
  return (
    <div className="detailed-data-table">
      <div className="table-header">
        <div>
          <h3>📋 Datos Detallados con Filtros</h3>
          {fuentePrecio && fuentePrecio !== 'Publications (actual)' && (
            <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
              💵 Usando: <strong>{fuentePrecio}</strong>
            </div>
          )}
        </div>
        <button onClick={handleExport} className="btn btn-primary export-btn">
          📥 Exportar CSV
        </button>
      </div>
      
      <div className="filters-section">
        <div className="filter-item">
          <label>Proveedor:</label>
          <select 
            value={filters.proveedor_filter || 'Todos'}
            onChange={(e) => handleFilterChange('proveedor_filter', e.target.value === 'Todos' ? null : e.target.value)}
            className="filter-select"
          >
            {availableProviders.map(prov => (
              <option key={prov} value={prov}>{prov}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-item">
          <label>Utilidad mínima ($):</label>
          <input 
            type="number" 
            value={filters.min_utility}
            onChange={(e) => handleFilterChange('min_utility', e.target.value)}
            placeholder="Sin mínimo"
            className="filter-input"
            step="0.01"
          />
        </div>
        
        <div className="filter-item">
          <label>Utilidad máxima ($):</label>
          <input 
            type="number" 
            value={filters.max_utility}
            onChange={(e) => handleFilterChange('max_utility', e.target.value)}
            placeholder="Sin máximo"
            className="filter-input"
            step="0.01"
          />
        </div>
        
        <div className="filter-item">
          <label>Registros por página:</label>
          <select
            value={filters.limit}
            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
            className="filter-select"
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="250">250</option>
          </select>
        </div>
      </div>
      
      {pagination && (
        <div className="table-info">
          Mostrando <strong>{data.length}</strong> de <strong>{pagination.total_count.toLocaleString()}</strong> registros
          {filters.proveedor_filter && filters.proveedor_filter !== 'Todos' && (
            <span className="filter-badge">
              Proveedor: {filters.proveedor_filter}
            </span>
          )}
          {filters.min_utility && (
            <span className="filter-badge">
              Min: ${filters.min_utility}
            </span>
          )}
          {filters.max_utility && (
            <span className="filter-badge">
              Max: ${filters.max_utility}
            </span>
          )}
        </div>
      )}
      
      <div className="table-wrapper">
        {data.length > 0 ? (
          <AdvancedDataTable 
            data={data}
            columns={columns}
            pageSize={filters.limit}
            enableSearch={false}
            enableSort={true}
          />
        ) : (
          <div className="no-data">
            No se encontraron registros con los filtros aplicados
          </div>
        )}
      </div>
      
      {pagination && pagination.total_pages > 1 && (
        <div className="pagination-controls">
          <button 
            onClick={() => handlePageChange(0)}
            disabled={pagination.offset === 0 || loading}
            className="btn btn-pagination"
            title="Primera página"
          >
            ⏮️ Primera
          </button>
          
          <button 
            onClick={() => handlePageChange(Math.max(0, pagination.offset - pagination.limit))}
            disabled={pagination.offset === 0 || loading}
            className="btn btn-pagination"
            title="Página anterior"
          >
            ◀️ Anterior
          </button>
          
          <span className="pagination-info">
            Página <strong>{pagination.current_page}</strong> de <strong>{pagination.total_pages}</strong>
          </span>
          
          <button 
            onClick={() => handlePageChange(pagination.offset + pagination.limit)}
            disabled={!pagination.has_more || loading}
            className="btn btn-pagination"
            title="Página siguiente"
          >
            Siguiente ▶️
          </button>
          
          <button 
            onClick={() => handlePageChange((pagination.total_pages - 1) * pagination.limit)}
            disabled={!pagination.has_more || loading}
            className="btn btn-pagination"
            title="Última página"
          >
            Última ⏭️
          </button>
        </div>
      )}
      
      {loading && data.length > 0 && (
        <div className="loading-inline">
          <div className="spinner-small"></div>
          Cargando...
        </div>
      )}
    </div>
  );
};

export default DetailedDataTable;

